import { useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import type {
  NotificationType,
  PlatformNotification,
} from '~/features/notifications/types';
import type { AiWorkflowBuildStatus } from '~/redux/slices/ai-workflow-builds/types';

import { useRegisterNotificationSource } from '~/features/notifications/NotificationCenterContext';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { k2 } from '~/redux/api/k2';
import { RoutePathType } from '~/routing/enums';

import {
  AiWorkflowBuildNotificationSubscription,
  useAiWorkflowBuildNotifications,
} from './notificationsClient';

const SOURCE_ID = 'ai-workflow-builds';

const messages = defineMessages({
  creatingWorkflow: {
    id: 'AiWorkflowBuildsNotificationSource.creatingWorkflow',
    defaultMessage: 'Creating new workflow',
    description: 'Title for an in-progress AI workflow build notification',
  },
  readyTitle: {
    id: 'AiWorkflowBuildsNotificationSource.readyTitle',
    defaultMessage: '{name} is ready',
    description:
      'Title for a ready AI workflow build notification, where {name} is the (emphasized) workflow name',
  },
  failedTitle: {
    id: 'AiWorkflowBuildsNotificationSource.failedTitle',
    defaultMessage: 'Workflow build failed',
    description: 'Title for a failed AI workflow build notification',
  },
});

// Renders "<name> is ready" once the real, AI-generated workflow name resolves
// from k2. The build's own title is a client-side placeholder (the user's typed
// prompt/filename) that never reconciles with the real name during a live
// session, so we render nothing until k2 returns rather than flashing a
// placeholder that then swaps out.
function ReadyBuildTitle({ workflowId }: { workflowId: string | null }) {
  const { data } = k2.useGetWorkflowQuery(
    { workflowId: workflowId ?? '' },
    { skip: !workflowId },
  );
  const name = data?.data.attributes.name;
  if (!name) return null;
  return (
    <FormattedMessage
      {...messages.readyTitle}
      values={{ name: <strong>{name}</strong> }}
    />
  );
}

const TYPE_BY_BUILD_STATUS: Record<AiWorkflowBuildStatus, NotificationType> = {
  failed: 'failure',
  pending_scan: 'ai',
  queued: 'ai',
  ready: 'success',
  running: 'ai',
};

function AiWorkflowBuildsNotificationSourceInner() {
  const intl = useIntl();
  const { markNotificationRead, notifications } =
    useAiWorkflowBuildNotifications();

  const platformNotifications = useMemo<PlatformNotification[]>(
    () =>
      notifications.map((notification) => {
        const { build_id, status, workflow_id } = notification;
        const isReady = status === 'ready';

        let notificationTitle: PlatformNotification['title'];
        if (isReady) {
          notificationTitle = <ReadyBuildTitle workflowId={workflow_id} />;
        } else if (status === 'failed') {
          notificationTitle = intl.formatMessage(messages.failedTitle);
        } else {
          notificationTitle = intl.formatMessage(messages.creatingWorkflow);
        }

        return {
          id: build_id,
          type: TYPE_BY_BUILD_STATUS[status],
          title: notificationTitle,
          createdAt: notification.created_at,
          isRead: notification.is_read,
          href:
            isReady && workflow_id
              ? RoutePathType.WorkflowCLMEvolutionWorkflowsDetail.replace(
                  ':workflowId',
                  workflow_id,
                )
              : undefined,
          onMarkAsRead: () => {
            markNotificationRead(build_id).catch(() => {});
          },
        };
      }),
    [notifications, markNotificationRead, intl],
  );

  useRegisterNotificationSource(SOURCE_ID, platformNotifications);

  return (
    <>
      {notifications.map((notification) => (
        <AiWorkflowBuildNotificationSubscription
          key={notification.build_id}
          notification={notification}
        />
      ))}
    </>
  );
}

export function AiWorkflowBuildsNotificationSource() {
  const isEnabled = useFlag(FlagType.CLMEWorkflowBuilderAiStart);
  if (!isEnabled) return null;
  return <AiWorkflowBuildsNotificationSourceInner />;
}
